/**
 * 权限审计日志组件
 * 查看权限评估的审计记录
 */
import React, { useState, useEffect } from 'react';
import {
  Card, Table, Form, Input, Select, Button, Space,
  Tag, DatePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getPermissionAuditLogs } from '../../api/permissionEngine';
import type {
  PermissionAuditLogInfo,
  PermissionAuditLogQueryParams,
} from '../../api/types';

const { RangePicker } = DatePicker;

const resultColorMap: Record<string, string> = {
  allow: 'success',
  deny: 'error',
  error: 'warning',
};

const sourceColorMap: Record<string, string> = {
  role: 'blue',
  override: 'purple',
  data_scope: 'orange',
  field: 'cyan',
  api: 'geekblue',
};

const PermissionAuditLog: React.FC = () => {
  const [list, setList] = useState<PermissionAuditLogInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [searchForm] = Form.useForm();

  const fetchList = async () => {
    setLoading(true);
    try {
      const formValues = searchForm.getFieldsValue();
      const params: PermissionAuditLogQueryParams = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      };

      if (formValues.userId) params.userId = Number(formValues.userId);
      if (formValues.permissionCode) params.permissionCode = formValues.permissionCode;
      if (formValues.action) params.action = formValues.action;
      if (formValues.result) params.result = formValues.result;
      if (formValues.source) params.source = formValues.source;
      if (formValues.timeRange?.length === 2) {
        params.startTime = formValues.timeRange[0].toISOString();
        params.endTime = formValues.timeRange[1].toISOString();
      }

      const res = await getPermissionAuditLogs(params);
      if (res?.data) {
        const responseData = res.data as any;
        setList(responseData.list || []);
        setTotal(responseData.total || 0);
      }
    } catch (error) {
      console.error('获取审计日志失败', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchList();
  };

  const handleReset = () => {
    searchForm.resetFields();
    setPagination({ ...pagination, current: 1 });
    fetchList();
  };

  const columns: ColumnsType<PermissionAuditLogInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: '用户',
      dataIndex: 'userName',
      width: 100,
      render: (name, record) => name || `用户#${record.userId}`,
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 80,
      render: (action) => <Tag>{action}</Tag>,
    },
    {
      title: '权限码',
      dataIndex: 'permissionCode',
      width: 180,
      ellipsis: true,
      render: (code) => <code style={{ fontSize: 12 }}>{code}</code>,
    },
    {
      title: '资源',
      dataIndex: 'resource',
      width: 180,
      ellipsis: true,
    },
    {
      title: '方法',
      dataIndex: 'method',
      width: 80,
      render: (method) => {
        const colorMap: Record<string, string> = {
          GET: 'green', POST: 'blue', PUT: 'orange', DELETE: 'red', PATCH: 'purple',
        };
        return <Tag color={colorMap[method] || 'default'}>{method}</Tag>;
      },
    },
    {
      title: '结果',
      dataIndex: 'result',
      width: 80,
      render: (result) => (
        <Tag color={resultColorMap[result] || 'default'}>{result}</Tag>
      ),
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 100,
      render: (source) => (
        <Tag color={sourceColorMap[source] || 'default'}>{source}</Tag>
      ),
    },
    {
      title: '操作人',
      dataIndex: 'operatorName',
      width: 100,
      render: (name) => name || '-',
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      width: 130,
      render: (ip) => ip || '-',
    },
    {
      title: '详情',
      dataIndex: 'details',
      width: 200,
      ellipsis: true,
      render: (details) => details || '-',
    },
    {
      title: '时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  return (
    <div>
      <Card title="权限审计日志" bordered={false}>
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
        >
          <Form.Item name="userId">
            <Input placeholder="用户ID" allowClear style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name="permissionCode">
            <Input placeholder="权限码" allowClear style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="action">
            <Select placeholder="操作" style={{ width: 100 }} allowClear>
              <Select.Option value="check">check</Select.Option>
              <Select.Option value="grant">grant</Select.Option>
              <Select.Option value="deny">deny</Select.Option>
              <Select.Option value="revoke">revoke</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="result">
            <Select placeholder="结果" style={{ width: 100 }} allowClear>
              <Select.Option value="allow">允许</Select.Option>
              <Select.Option value="deny">拒绝</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="source">
            <Select placeholder="来源" style={{ width: 120 }} allowClear>
              <Select.Option value="role">角色权限</Select.Option>
              <Select.Option value="override">权限覆盖</Select.Option>
              <Select.Option value="data_scope">数据范围</Select.Option>
              <Select.Option value="api">API检查</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="timeRange">
            <RangePicker showTime style={{ width: 320 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">搜索</Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 表格 */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1600 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default PermissionAuditLog;
